import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, RotateCcw, XCircle } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import StatusBadge from '@/components/StatusBadge'
import { controlCenterApi } from '@/lib/api'

type McpTool = {
  id?: string
  name: string
  description?: string
  risk_tier?: string
  approval_policy?: string
  owner_service?: string
  agent_registry_id?: string
  status?: string
}

type McpInvocation = {
  id: string
  request_id?: string
  tool_name?: string
  status?: string
  risk_tier?: string
  actor_id?: string
  workspace_id?: string
  created_at?: string
}

type McpTraceJob = {
  id?: string
  status?: string
}

type McpTraceEvent = {
  event_type?: string
  summary?: string
  created_at?: string
}

type McpTraceData = {
  invocation?: McpInvocation
  jobs?: McpTraceJob[]
  events?: McpTraceEvent[]
}

function rows<T>(value: unknown, key: string): T[] {
  if (Array.isArray(value)) return value as T[]
  if (value && typeof value === 'object' && Array.isArray((value as Record<string, unknown>)[key])) {
    return (value as Record<string, unknown>)[key] as T[]
  }
  return []
}

function prettyTime(value?: string) {
  if (!value) return '-'
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? value : new Date(parsed).toLocaleString()
}

function canRetry(status?: string) {
  return ['failed', 'timeout', 'rejected', 'cancelled'].includes(String(status ?? '').toLowerCase())
}

function canCancel(status?: string) {
  return ['queued', 'approval_required', 'failed', 'timeout', 'rejected'].includes(String(status ?? '').toLowerCase())
}

function traceData(value: unknown): McpTraceData {
  return value && typeof value === 'object' ? value as McpTraceData : {}
}

function firstEvent(events: McpTraceEvent[] | undefined, eventType: string) {
  return (events ?? []).find((event) => String(event.event_type ?? '').toLowerCase() === eventType)
}

function eventSummary(event: McpTraceEvent | undefined, fallback: string) {
  if (!event) return 'not observed'
  const summary = String(event.summary ?? event.event_type ?? fallback)
  return summary.length > 64 ? `${summary.slice(0, 61)}...` : summary
}

function buildTraceStateSummary(value: unknown) {
  const trace = traceData(value)
  const primaryJob = trace.jobs?.[0]
  const leaseEvent = firstEvent(trace.events, 'leased')
  const heartbeatEvent = firstEvent(trace.events, 'heartbeat')
  const completedEvent = firstEvent(trace.events, 'completed')

  return {
    job: primaryJob?.id
      ? `${primaryJob.id} / ${primaryJob.status ?? 'unknown'}`
      : 'not observed',
    lease: eventSummary(leaseEvent, 'leased'),
    heartbeat: eventSummary(heartbeatEvent, 'heartbeat'),
    result: trace.invocation?.status ?? completedEvent?.event_type ?? 'not observed',
  }
}

function sanitizeTraceForDisplay(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitizeTraceForDisplay)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !/^lease_?token$/i.test(key))
        .map(([key, entry]) => [key, sanitizeTraceForDisplay(entry)]),
    )
  }
  if (typeof value === 'string') {
    return value
      .replace(/(bearer\s+)(?!\[REDACTED\])[a-z0-9._~+/-]+/gi, '$1[REDACTED]')
      .replace(/-----BEGIN [^-]+-----[\s\S]*?-----END [^-]+-----/g, '[REDACTED_PRIVATE_KEY]')
      .replace(/\b[A-Z0-9_]*(?:TOKEN|SECRET|PASSWORD|KEY)[A-Z0-9_]*=(?!\[REDACTED\]|<[^>]+>)[^\s`'"]+/gi, (match) => `${match.split('=')[0]}=[REDACTED]`)
  }
  return value
}

export default function McpInstanceActions() {
  const [selectedInvocationId, setSelectedInvocationId] = useState<string | null>(null)
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const toolsQuery = useQuery({
    queryKey: ['mcp', 'tools', 'catalog'],
    queryFn: controlCenterApi.listMcpToolCatalog,
    refetchInterval: 30_000,
  })
  const invocationsQuery = useQuery({
    queryKey: ['mcp', 'invocations'],
    queryFn: controlCenterApi.listMcpInvocations,
    refetchInterval: 15_000,
  })
  const traceQuery = useQuery({
    queryKey: ['mcp', 'invocation-trace', selectedInvocationId],
    queryFn: () => controlCenterApi.getMcpInvocationTrace(selectedInvocationId as string),
    enabled: Boolean(selectedInvocationId),
  })

  const tools = rows<McpTool>(toolsQuery.data, 'tools')
  const invocations = rows<McpInvocation>(invocationsQuery.data, 'invocations')
  const selectedTraceInvocation = traceQuery.data && typeof traceQuery.data === 'object'
    ? (traceQuery.data as { invocation?: McpInvocation }).invocation
    : undefined
  const traceStateSummary = buildTraceStateSummary(traceQuery.data)

  async function refreshData() {
    await Promise.all([
      toolsQuery.refetch(),
      invocationsQuery.refetch(),
      selectedInvocationId ? traceQuery.refetch() : Promise.resolve(),
    ])
  }

  async function runAction(kind: 'retry' | 'cancel', id: string) {
    setBusyAction(`${kind}:${id}`)
    try {
      if (kind === 'retry') {
        await controlCenterApi.retryMcpInvocation(id)
      } else {
        await controlCenterApi.cancelMcpInvocation(id)
      }
      await refreshData()
    } finally {
      setBusyAction(null)
    }
  }

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="MCP Actions"
        description="Production fleet instance actions, recent invocations, and sanitized execution evidence."
        actions={
          <button
            type="button"
            onClick={() => {
              void refreshData()
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm"
            aria-label="Refresh MCP actions"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        }
      />

      {(toolsQuery.isError || invocationsQuery.isError) ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load MCP instance action data.
        </div>
      ) : null}

      <section className="space-y-3">
        <h3 className="text-base font-semibold">Action Catalog</h3>
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="px-4 py-2.5 font-medium">Tool</th>
                <th className="px-4 py-2.5 font-medium">Risk</th>
                <th className="px-4 py-2.5 font-medium">Approval</th>
                <th className="px-4 py-2.5 font-medium">Owner</th>
                <th className="px-4 py-2.5 font-medium">Agent</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {toolsQuery.isLoading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Loading...</td></tr>
              ) : tools.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No instance actions published yet.</td></tr>
              ) : tools.map((tool) => (
                <tr key={tool.id ?? tool.name} className="border-t border-border">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{tool.name}</div>
                    <div className="mt-1 max-w-2xl text-xs text-muted-foreground">{tool.description}</div>
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge status={tool.risk_tier ?? 'unknown'} /></td>
                  <td className="px-4 py-2.5 text-muted-foreground">{tool.approval_policy ?? '-'}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{tool.owner_service ?? '-'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{tool.agent_registry_id ?? '-'}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={tool.status ?? 'unknown'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
        <div className="space-y-3">
          <h3 className="text-base font-semibold">Recent Invocations</h3>
          <div className="overflow-hidden rounded-md border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-4 py-2.5 font-medium">Request</th>
                  <th className="px-4 py-2.5 font-medium">Tool</th>
                  <th className="px-4 py-2.5 font-medium">Actor</th>
                  <th className="px-4 py-2.5 font-medium">Workspace</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Created</th>
                  <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invocationsQuery.isLoading ? (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Loading...</td></tr>
                ) : invocations.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No invocations recorded yet.</td></tr>
                ) : invocations.map((invocation) => (
                  <tr
                    key={invocation.id}
                    className="border-t border-border cursor-pointer hover:bg-muted/30"
                    onClick={() => setSelectedInvocationId(invocation.id)}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs">{invocation.request_id ?? invocation.id}</td>
                    <td className="px-4 py-2.5">{invocation.tool_name ?? '-'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{invocation.actor_id ?? '-'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{invocation.workspace_id ?? '-'}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={invocation.status ?? 'unknown'} /></td>
                    <td className="px-4 py-2.5 text-muted-foreground">{prettyTime(invocation.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          disabled={!canRetry(invocation.status) || busyAction !== null}
                          onClick={(event) => {
                            event.stopPropagation()
                            void runAction('retry', invocation.id)
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label={`Retry ${invocation.request_id ?? invocation.id}`}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={!canCancel(invocation.status) || busyAction !== null}
                          onClick={(event) => {
                            event.stopPropagation()
                            void runAction('cancel', invocation.id)
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label={`Cancel ${invocation.request_id ?? invocation.id}`}
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-base font-semibold">Trace Detail</h3>
          <div className="min-h-80 rounded-md border border-border bg-card p-4">
            {!selectedInvocationId ? (
              <p className="text-sm text-muted-foreground">Select an invocation to inspect sanitized evidence.</p>
            ) : traceQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading trace...</p>
            ) : traceQuery.isError ? (
              <p className="text-sm text-destructive">Failed to load trace.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={!canRetry(selectedTraceInvocation?.status) || busyAction !== null}
                    onClick={() => selectedInvocationId && void runAction('retry', selectedInvocationId)}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Retry
                  </button>
                  <button
                    type="button"
                    disabled={!canCancel(selectedTraceInvocation?.status) || busyAction !== null}
                    onClick={() => selectedInvocationId && void runAction('cancel', selectedInvocationId)}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel
                  </button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div aria-label="Trace job state" className="min-w-0 border-l-2 border-border pl-3">
                    <div className="text-xs font-medium uppercase text-muted-foreground">Job</div>
                    <div className="truncate text-sm">{traceStateSummary.job}</div>
                  </div>
                  <div aria-label="Trace lease state" className="min-w-0 border-l-2 border-border pl-3">
                    <div className="text-xs font-medium uppercase text-muted-foreground">Lease</div>
                    <div className="truncate text-sm">{traceStateSummary.lease}</div>
                  </div>
                  <div aria-label="Trace heartbeat state" className="min-w-0 border-l-2 border-border pl-3">
                    <div className="text-xs font-medium uppercase text-muted-foreground">Heartbeat</div>
                    <div className="truncate text-sm">{traceStateSummary.heartbeat}</div>
                  </div>
                  <div aria-label="Trace result state" className="min-w-0 border-l-2 border-border pl-3">
                    <div className="text-xs font-medium uppercase text-muted-foreground">Result</div>
                    <div className="truncate text-sm">{traceStateSummary.result}</div>
                  </div>
                </div>
                <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap break-words text-xs text-muted-foreground">
                  {JSON.stringify(sanitizeTraceForDisplay(traceQuery.data), null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
